import React from "react"
import ArticleMeta from "../../components/article-meta"
import CommentsForm from "../../components/comments-form"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import { Articles } from "../../data/articles"

const article = Articles.getBySlug("what-leaders-really-do")

export default () => (
  <Layout>
    <SEO
      title={article.title}
      keywords={[article.title, ...article.categories]}
    />
    <div id="section">
      <div className="main-article">
        <ArticleMeta article={article} />

        <div className="article-content">
          <img
            src={article.image}
            className="attachment-wide-thumbnail"
            alt={article.title}
          />
          <p>
            Leadership is different from management, but not for the reasons
            most people think. Leadership isn’t mystical and mysterious. It has
            nothing to do with having “charisma” or other exotic personality
            traits. It is not the province of a chosen few. Nor is leadership
            necessarily better than management or a replacement for it.
          </p>
          <p>
            Rather, leadership and management are two distinctive and
            complementary systems of action. Each has its own function and
            characteristic activities. Both are necessary for success in an
            increasingly complex and volatile business environment.
          </p>
          <p>
            Most U.S. corporations today are over-managed and underled. They
            need to develop their capacity to exercise leadership. Successful
            corporations don’t wait for leaders to come along. They actively
            seek out people with leadership potential and expose them to career
            experiences designed to develop that potential. Indeed, with careful
            selection, nurturing, and encouragement, dozens of people can play
            important leadership roles in a business organization.
          </p>
          <p>
            But while improving their ability to lead, companies should remember
            that strong leadership with weak management is no better, and is
            sometimes actually worse, than the reverse. The real challenge is to
            combine strong leadership and strong management and use each to
            balance the other.
          </p>
          <p>
            Of course, not everyone can be good at both leading and managing.
            Some people have the capacity to become excellent managers but not
            strong leaders. Others have great leadership potential but, for a
            variety of reasons, have great difficulty becoming strong managers.
            Smart companies value both kinds of people and work hard to make
            them a part of the team.
          </p>
          <p>
            But when it comes to preparing people for executive jobs, such
            companies rightly ignore the recent literature that says people
            cannot manage and lead. They try to develop leader-managers. Once
            companies understand the fundamental difference between leadership
            and management, they can begin to groom their top people to provide
            both.
          </p>

          <h2>The Difference Between Management and Leadership</h2>
          <p>
            Management is about coping with complexity. Its practices and
            procedures are largely a response to one of the most significant
            developments of the twentieth century: the emergence of large
            organizations. Without good management, complex enterprises tend to
            become chaotic in ways that threaten their very existence. Good
            management brings a degree of order and consistency to key
            dimensions like the quality and profitability of products.
          </p>
          <p>
            Management is about coping with complexity. Leadership, by contrast,
            is about coping with change.
          </p>
          <p>
            Leadership, by contrast, is about coping with change. Part of the
            reason it has become so important in recent years is that the
            business world has become more competitive and more volatile. Faster
            technological change, greater international competition, the
            deregulation of markets, overcapacity in capital-intensive
            industries, an unstable oil cartel, raiders with junk bonds, and the
            changing demographics of the work-force are among the many factors
            that have contributed to this shift. The net result is that doing
            what was done yesterday, or doing it 5% better, is no longer a
            formula for success. Major changes are more and more necessary to
            survive and compete effectively in this new environment. More change
            always demands more leadership.
          </p>
          <p>
            Consider a simple military analogy: A peacetime army can usually
            survive with good administration and management up and down the
            hierarchy, coupled with good leadership concentrated at the very
            top. A wartime army, however, needs competent leadership at all
            levels. No one yet has figured out how to manage people effectively
            into battle; they must be led.
          </p>
          <p>
            These two different functions—coping with complexity and coping with
            change—shape the characteristic activities of management and
            leadership. Each system of action involves deciding what needs to be
            done, creating networks of people and relationships that can
            accomplish an agenda, and then trying to ensure that those people
            actually do the job. But each accomplishes these three tasks in
            different ways.
          </p>
          <p>
            Companies manage complexity first by planning and budgeting—setting
            targets or goals for the future (typically for the next month or
            year), establishing detailed steps for achieving those targets, and
            then allocating resources to accomplish those plans. By contrast,
            leading an organization to constructive change begins by setting a
            direction—developing a vision of the future (often the distant
            future) along with strategies for producing the changes needed to
            achieve that vision.
          </p>

          <CommentsForm title={article.title} />
        </div>
      </div>
    </div>
  </Layout>
)
